import { makeStyles, Popover, Theme, Typography } from '@material-ui/core'
import { ArrowDownward } from '@material-ui/icons'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import React, { useCallback, useEffect } from 'react'
import MoveBlockActions from './MoveBlockActions'

interface MoveBlockPopoverProps {
    anchorElement: Element | null
    isOpen: boolean
    onClose: () => void
    onMoveUp: () => void
    onMoveDown: () => void
    isMoveUpDisabled: boolean
    isMoveDownDisabled: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    popoverContainer: {
        padding: theme.spacing(1.5, 2),
        borderRadius: '10px',
        boxShadow: '0px 3px 15px 0px #00000059',
    },
    itemRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        minWidth: 180,
        marginBottom: theme.spacing(1),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    iconLabelGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    label: {
        fontSize: 12,
        color: '#211F20',
    },
    shortcut: {
        fontSize: 12,
        color: '#898989',
    },
    disabled: {
        opacity: 0.5,
        pointerEvents: 'none',
    },
}))

const MoveBlockPopover = ({
    anchorElement,
    isOpen,
    onClose,
    onMoveUp,
    onMoveDown,
    isMoveUpDisabled,
    isMoveDownDisabled,
}: MoveBlockPopoverProps) => {
    const classes = useStyles()

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!isOpen) return // ignore when closed
            if (event.shiftKey && event.altKey) {
                if (event.key === 'ArrowUp' && !isMoveUpDisabled) {
                    event.preventDefault()
                    onMoveUp()
                } else if (event.key === 'ArrowDown' && !isMoveDownDisabled) {
                    event.preventDefault()
                    onMoveDown()
                }
            }
        },
        [isMoveUpDisabled, isMoveDownDisabled, onMoveUp, onMoveDown]
    )

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown)
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [isOpen, handleKeyDown])

    return (
        <Popover
            open={isOpen}
            anchorEl={anchorElement}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'center',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'center',
                horizontal: 'left',
            }}
            classes={{ paper: classes.popoverContainer }}
            role="dialog"
            aria-labelledby="move-block-popover-title"
        >
            <Typography
                id="move-block-popover-title"
                variant="h6"
                component="h2"
                style={{ display: 'none' }}
            >
                Move Block Options
            </Typography>
            <MoveBlockActions
                isDisabled={isMoveUpDisabled}
                onClick={onMoveUp}
                label="Up"
                shortcut="Shift+Alt+Up"
                Icon={ArrowUpwardIcon}
                classes={classes}
            />
            <MoveBlockActions
                isDisabled={isMoveDownDisabled}
                onClick={onMoveDown}
                label="Down"
                shortcut="Shift+Alt+Down"
                Icon={ArrowDownward}
                classes={classes}
            />
        </Popover>
    )
}

export default MoveBlockPopover
